import { div, format, parse, type Micro } from "./money";

/**
 * Перевод «сумма в долларах -> размер ордера в share».
 *
 * ЗЕРКАЛО `signing.SharesForUSD` (services/internal/polymarket/signing/amounts.go),
 * и это не украшение архитектуры, а требование: трейдер готовит билет
 * пред-подписи заранее, ключом которого служит РАЗМЕР В SHARE. Разойдись две
 * реализации хоть на одну сотую — интерфейс отправит команду с размером, под
 * который билета нет, кэш промахнётся, и веха M5 выродится в подпись на месте
 * при полностью исправном коде. Видно это будет только по ноте
 * `presign_hit_ratio`, поэтому набор тестов здесь повторяет набор в Go
 * теми же числами.
 *
 * Правило: share = вниз($ / цена, 0.01).
 *
 * Две сотых знака — не выбор интерфейса, а точность размера, которую принимает
 * биржа: во всех шести конфигурациях округления Polymarket размер разрешён с
 * двумя знаками, независимо от шага цены.
 *
 * Вниз, а не к ближайшему: округление вверх потратило бы больше названной суммы.
 */

/** Знаков в размере ордера. Совпадает с signing.sizeDecimals. */
const SIZE_DECIMALS = 2;

/**
 * Шаг размера в микро-единицах: 0.01.
 *
 * Экспортируется, потому что размер считается двумя способами: из суммы в
 * долларах (покупка, sharesForUsd) и как доля позиции (продажа,
 * lib/position.ts). Спрячь шаг здесь — и второе правило рано или поздно
 * округлит по-своему, а разошедшееся округление размера биржа отвергает
 * ордером целиком.
 */
export const SIZE_STEP = 10_000;

/**
 * Округление размера ВНИЗ до шага.
 *
 * Вниз везде, где размер выводится из чего-то другого: из суммы округление
 * вверх потратило бы больше названного, из позиции — попыталось бы продать
 * больше, чем есть.
 */
export function floorToSizeStep(m: Micro): Micro {
  return Math.floor(m / SIZE_STEP) * SIZE_STEP;
}

/**
 * Размер в share под сумму и цену, обе в микро-единицах.
 * null — если сумма или цена бессмысленны либо суммы не хватает даже на 0.01 share.
 */
export function sharesForUsdMicro(usd: Micro, price: Micro): Micro | null {
  if (usd <= 0) return null;
  // Цена вне открытого интервала (0, 1) — это не ордер: те же границы, что у
  // Amounts на стороне трейдера.
  if (price <= 0 || price >= 1_000_000) return null;

  const raw = div(usd, price);
  if (raw === null) return null;

  const size = floorToSizeStep(raw);
  return size > 0 ? size : null;
}

/**
 * То же самое строками — в том виде, в каком величины ходят по проводу.
 * null означает «такой ордер не собрать», и вызывающий обязан это показать, а
 * не подставить ноль.
 */
export function sharesForUsd(usd: string, price: string): string | null {
  const u = parse(usd);
  const p = parse(price);
  if (u === null || p === null) return null;
  const size = sharesForUsdMicro(u, p);
  return size === null ? null : format(size);
}

/** Знаков в размере — для форматирования полей ввода и подписей. */
export const SIZE_FRACTION_DIGITS = SIZE_DECIMALS;
